import { useMemo } from 'react';

import {
  FEATURE_FLAGS_FOR_SELLER,
  ROLES,
} from '@savgroup-front-common/constants';
import { PERMISSIONS } from '@savgroup-front-common/constants/control';
import {
  useHasPermission,
  useHasSomePermissions,
  useHasSomeRoles,
  useIsSuperAdmin,
} from '@savgroup-front-common/core/hooks';
import { PRICING_PLAN } from '@savgroup-front-common/types';

import { useGetFeatureFlagsQuery } from '@/hooks';
import useHasBusinessIntelligencePermission from '@/hooks/useHasBusinessIntelligencePermission';
import { useHasPackingParcelModule } from '@/hooks/useHasPackingParcelModule';
import { useAppContext } from '@/view/app/App.context';
import useHasSellerSettingsPagePermission from '@/view/app/routes/hooks/useHasSellerSettingsPagePermission';

import { transformToMenu } from '../AppBarMenu.helpers';
import { MENU_NAMES, MenuConf } from '../AppBarMenu.types';

import messages from './messages';

export const useGetMenuItems = () => {
  const { selectedSeller } = useAppContext();
  const hasImportsPermission = useHasPermission(PERMISSIONS.IMPORT_MANAGEMENT);
  const isSuperAdmin = useIsSuperAdmin();
  const isFreemiumPricingPlan =
    selectedSeller?.pricingPlan === PRICING_PLAN.FREEMIUM && !isSuperAdmin;
  const isAdminDemo = useHasSomeRoles([
    ROLES.DEMO_ADMINISTRATOR,
    ROLES.ADMINISTRATOR,
  ]);
  const hasConsoleAccessRightsPermission = useHasPermission(
    PERMISSIONS.CONSOLE_ACCESS_RIGHTS,
  );

  const isPackingParcelModuleEnabled = useHasPackingParcelModule();
  const hasPackingParcelManagementPermission = useHasPermission(
    PERMISSIONS.PACKING_PARCEL_MANAGEMENT,
  );

  const hasSellerSettingsPermissions = useHasSellerSettingsPagePermission();

  const hasUserManagementPermission = useHasPermission(
    PERMISSIONS.ROLE_MANAGEMENT,
  );
  const haseSanityTestsSettingsPermission = useHasPermission(
    PERMISSIONS.SANITY_TESTS,
  );

  const hasSparePartPermission = useHasSomePermissions([
    PERMISSIONS.ACCESS_SPARE_PART_REQUEST,
    PERMISSIONS.ACCESS_SPARE_PART_STOCK_ITEMS,
    PERMISSIONS.MANAGE_SPARE_PART_CATALOG,
  ]);

  const hasSomeFilePermissions = useHasSomePermissions([
    PERMISSIONS.SEARCH_FILE,
    PERMISSIONS.GET_WORKFLOW,
    PERMISSIONS.WORKFLOW_MANAGEMENT,
  ]);

  const hasB2BBillingPermissions = useHasPermission(PERMISSIONS.B2B_BILLING);
  const hasBulkChangeAccessRights = useHasPermission(
    PERMISSIONS.BULK_CHANGE_ACCESS_RIGHTS,
  );

  const isHomeInterventionToggleFeatureEnabled = useGetFeatureFlagsQuery({
    featureFlag: [FEATURE_FLAGS_FOR_SELLER.HOME_INTERVENTION],
    sellerId: selectedSeller?.id,
    enabled: !!selectedSeller?.id,
  });

  const hasBusinessIntelligencePermission =
    useHasBusinessIntelligencePermission();

  const hasDisplayOrderTabPermission = useHasPermission(
    PERMISSIONS.DISPLAY_ORDER_TAB,
  );

  const menus = useMemo(() => {
    const items: MenuConf[] = [
      {
        nav: MENU_NAMES.FILES,
        expandIfAlone: true,
        permission: hasSomeFilePermissions || isAdminDemo,
      },
      {
        nav: MENU_NAMES.ORDERS,
        permission: hasDisplayOrderTabPermission || isAdminDemo,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
      },
      {
        nav: MENU_NAMES.BUSINESS_INTELLIGENCE,
        permission: hasBusinessIntelligencePermission || isAdminDemo,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
      },
      {
        nav: MENU_NAMES.TRANSPORT,
        permission:
          isPackingParcelModuleEnabled && hasPackingParcelManagementPermission,
      },
      {
        nav: MENU_NAMES.SPARE_PARTS,
        expandIfAlone: true,
        forceIconIfAlone: true,
        permission: hasSparePartPermission || isAdminDemo,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
      },
      {
        nav: MENU_NAMES.HOME_INTERVENTION,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
        permission: isHomeInterventionToggleFeatureEnabled || isSuperAdmin,
      },
      {
        nav: MENU_NAMES.INTERVENTION_REPORT,
        permission: hasB2BBillingPermissions || isSuperAdmin,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
      },
      {
        nav: MENU_NAMES.BILLINGS,
        permission: hasB2BBillingPermissions || isSuperAdmin,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
      },
      {
        nav: MENU_NAMES.JOBS,
        permission: hasBulkChangeAccessRights || isSuperAdmin,
        isDisabled: isFreemiumPricingPlan,
        toolTip: messages.lockedFeature,
      },
      {
        nav: MENU_NAMES.SETTINGS,
        permission:
          isAdminDemo ||
          hasSellerSettingsPermissions ||
          hasUserManagementPermission ||
          haseSanityTestsSettingsPermission ||
          hasImportsPermission ||
          hasConsoleAccessRightsPermission,
      },
    ];

    return transformToMenu(items);
  }, [
    hasSomeFilePermissions,
    isAdminDemo,
    hasDisplayOrderTabPermission,
    isFreemiumPricingPlan,
    hasBusinessIntelligencePermission,
    hasSparePartPermission,
    isHomeInterventionToggleFeatureEnabled,
    isSuperAdmin,
    hasB2BBillingPermissions,
    hasBulkChangeAccessRights,
    hasSellerSettingsPermissions,
    hasUserManagementPermission,
    haseSanityTestsSettingsPermission,
    hasImportsPermission,
    hasConsoleAccessRightsPermission,
    isPackingParcelModuleEnabled,
    hasPackingParcelManagementPermission,
  ]);

  return {
    menus,
  };
};
